import { User } from 'lucide-react';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import type { UserDto } from '@sbg/shared/schemas';
import type { AccountMenu } from '@/features/auth/AccountMenu';
import { sessionContent } from '@/features/auth/content/session';
import { initialsOf } from '@/features/auth/initials';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { interpolate } from '@/lib/i18n';

type AccountMenuComponent = typeof AccountMenu;

/**
 * The account menu (Radix dropdown + its styles) is a separate chunk: this file is in the entry
 * graph and must stay light. It is fetched the first time the account button is hovered, focused or
 * pressed, and kept for the rest of the visit.
 */
let menuChunk: Promise<AccountMenuComponent> | null = null;
function loadAccountMenu(): Promise<AccountMenuComponent> {
  menuChunk ??= import('@/features/auth/AccountMenu').then(
    (module) => module.AccountMenu,
    (error: unknown) => {
      menuChunk = null; // a dropped connection must not poison later attempts
      throw error;
    },
  );
  return menuChunk;
}

/**
 * Header account button. Signed out: nothing — signing in is for staff only and lives at `/admin` (D-391), so the public
 * header carries no login icon. Signed in (a staff member browsing the site): a round button showing the person's initials
 * whose accessible name says who is signed in; it opens the account menu (see `features/auth/AccountMenu`). If the menu chunk
 * cannot be fetched, the button simply goes to `/account` (which leads to the back-office) — it is never out of reach.
 */
export function AccountLink({ className }: { className?: string }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) return null;
  return <SignedInButton user={user} className={className} />;
}

function SignedInButton({ user, className }: { user: UserDto; className: string | undefined }) {
  const label = interpolate(sessionContent.trigger, { name: user.name });
  const navigate = useNavigate();
  const [Menu, setMenu] = useState<AccountMenuComponent | null>(null);
  // how the person asked for the menu before its chunk had arrived: null = not asked yet
  const [asked, setAsked] = useState<'pointer' | 'keyboard' | null>(null);

  const prefetch = (): void => {
    void loadAccountMenu().catch(() => undefined); // best effort — the press retries and reports
  };
  const open = (event: MouseEvent<HTMLButtonElement>): void => {
    // a click made with Enter or Space has no pointer position: detail is 0
    setAsked(event.detail === 0 ? 'keyboard' : 'pointer');
    loadAccountMenu().then(
      (component) => {
        setMenu(() => component); // a function as state must be wrapped, or React would call it
      },
      () => {
        setAsked(null);
        void navigate('/account');
      },
    );
  };

  const initials = initialsOf(user.name);
  const button = (
    <Button
      variant="ghost"
      round
      className={className}
      aria-label={label}
      aria-haspopup="menu"
      onPointerEnter={prefetch}
      onFocus={prefetch}
      // Once the menu is mounted, Radix owns aria-expanded and the press: leave those keys out.
      {...(Menu === null ? { 'aria-expanded': asked !== null, onClick: open } : {})}
    >
      <span
        className="grid size-8 place-items-center rounded-full bg-glass text-[0.75rem] font-medium tracking-wide text-fg ring-1 ring-accent/50"
        aria-hidden="true"
      >
        {initials === '' ? <User size={18} /> : initials}
      </span>
    </Button>
  );

  return Menu === null ? (
    button
  ) : (
    <Menu
      user={user}
      trigger={button}
      defaultOpen={asked !== null}
      focusFirst={asked === 'keyboard'}
    />
  );
}
